import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getAdminSessionDetail, type SessionDetail as SessionDetailData, type TranscriptEntryRead } from '@/api/client'
import { ReportView } from '@/components/interview/ReportView'
import { RecordingPlayer } from '@/components/interview/RecordingPlayer'
import { AppShell } from '@/components/layout/AppShell'
import { statusBadge, formatDateTime, formatDuration } from '@/lib/dashboard-utils'
import { usePageMeta } from '@/hooks/use-page-meta';

export function SessionDetail() {
  usePageMeta("Session — Aura Admin")
  const navigate = useNavigate()
  const { sessionId } = useParams<{ sessionId: string }>()
  const [data, setData] = useState<SessionDetailData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setData(null)
    setError(null)
    getAdminSessionDetail(sessionId)
      .then(d => { if (!cancelled) setData(d) })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load session detail:', {
          sessionId,
          status: err?.response?.status,
          message: err?.message
        })
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        if (err?.response?.status === 404) {
          setError('Session not found.')
          return
        }
        setError('Failed to load session detail. Please try again.')
      })
    return () => { cancelled = true }
  }, [sessionId, navigate])

  if (error) {
    return (
      <AppShell>
        <section className="panel">
          <div className="empty">
            <h3>Session not available</h3>
            <p>{error}</p>
            <div className="empty-cta">
              <Link to="/admin" className="wbtn wbtn-secondary">← Back to admin dashboard</Link>
            </div>
          </div>
        </section>
      </AppShell>
    )
  }

  if (!data) {
    return (
      <AppShell>
        <div className="wloading" role="status" aria-live="polite">
          <span className="wspinner" aria-hidden="true"></span>
          <span>Loading session…</span>
        </div>
      </AppShell>
    )
  }

  // Once the report is available, render the full ReportView on top so admins
  // get the same polished report UI the candidate sees — then layer the
  // transcript and metadata beneath.
  const status = statusBadge(data.status)
  return (
    <AppShell active="admin">
      <div>
        <Link to="/admin" className="link-action inline-flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back to admin dashboard
        </Link>

        {/* The page's own h1: ReportView below is embedded with
            headingLevel="h2" so the page keeps exactly one <h1> (the old
            <p> demotion left no-report sessions with zero headings). */}
        <header className="screen-head mt-3">
          <div>
            <h1>{data.candidate_name}</h1>
            <p>
              <span className={`${status.cls} mr-2.5`}>{status.text}</span>
              {data.user_email || 'no email'}
              {data.report && <> · Score {data.report.overall_score} · {data.report.recommendation}</>}
            </p>
          </div>
        </header>

        <div className="overview-strip wmeta-strip" role="group" aria-label="Session metadata">
          <article className="overview-card">
            <span className="overview-copy"><strong className="wmeta-strong">{data.candidate_name}</strong><span>Candidate</span></span>
          </article>
          <article className="overview-card">
            <span className="overview-copy"><strong className="wmeta-strong wmeta-email">{data.user_email || '—'}</strong><span>Account</span></span>
          </article>
          <article className="overview-card">
            <span className="overview-copy"><strong className="wmeta-strong">{formatDateTime(data.created_at)}</strong><span>Created</span></span>
          </article>
          <article className="overview-card">
            <span className="overview-copy"><strong className="wmeta-strong">{formatDateTime(data.completed_at)}</strong><span>Completed</span></span>
          </article>
          <article className="overview-card">
            <span className="overview-copy"><strong className="wmeta-strong">{formatDuration(
              data.completed_at && data.created_at
                ? Math.round((new Date(data.completed_at).getTime() - new Date(data.created_at).getTime()) / 1000)
                : null
            )}</strong><span>Duration</span></span>
          </article>
        </div>

        {data.report ? (
          <section aria-label="Report" className="mt-4">
            <ReportView
              report={data.report}
              sessionId={data.session_id}
              completedAt={data.completed_at}
              createdAt={data.created_at}
              transcript={data.transcript}
              integrityEvents={data.integrity_events}
              proctored={data.proctored}
              skills={data.plan?.extracted_skills}
              headingLevel="h2"
              extraActions={<RecordingPlayer sessionId={data.session_id} candidateName={data.candidate_name} />}
            />
          </section>
        ) : (
          <section className="panel mt-4">
            <div className="empty">
              <h3>No report yet</h3>
              <p>This session is {data.status}. A report will be available once the interview is completed.</p>
            </div>
          </section>
        )}

        {data.transcript && data.transcript.length > 0 ? (
          <section className="panel mt-4" aria-label="Transcript">
            <div className="panel-head">
              <h2>Transcript</h2>
              <span className="panel-meta">{data.transcript.length} lines</span>
            </div>
            <div className="wtranscript" role="log" aria-label="Interview transcript">
              {data.transcript.map((entry: TranscriptEntryRead, i: number) => {
                const isAssistant = entry.speaker.toLowerCase().includes('aura') ||
                  entry.speaker.toLowerCase().includes('agent') ||
                  entry.speaker.toLowerCase().includes('assistant')
                return (
                  <div key={i} className={`wtranscript-line ${isAssistant ? 'assistant' : ''}`}>
                    <span className="who">{entry.speaker}</span>
                    <span className="text">{entry.text}</span>
                  </div>
                )
              })}
            </div>
          </section>
        ) : data.status === 'completed' ? (
          <section className="panel mt-4" aria-label="Transcript">
            <div className="empty">
              <p>No transcript was captured for this session.</p>
            </div>
          </section>
        ) : null}
      </div>
    </AppShell>
  )
}
